import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { buildGscRedirectUri } from "@/lib/gscOAuth";

const GscCallback = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<"working" | "error" | "done">("working");
  const [error, setError] = useState<string | null>(null);
  const [connectedInfo, setConnectedInfo] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");
      const state = params.get("state");
      const oauthError = params.get("error");

      if (oauthError) {
        setStatus("error");
        setError(oauthError);
        return;
      }
      if (!code || !state) {
        setStatus("error");
        setError("Missing code or state in callback URL.");
        return;
      }

      // Verify the active Supabase session matches the state we sent. State is "<uid>" for a
      // self-connect, or "<uid>:<clientId>" for an admin-targeted reconnect (Option 2) — in
      // both cases the uid half must match this session.
      const { data: auth } = await supabase.auth.getUser();
      const uid = auth.user?.id;
      if (!uid) {
        setStatus("error");
        setError("Not logged in. Please log in and reconnect GSC.");
        return;
      }
      const stateUid = state.includes(":") ? state.slice(0, state.indexOf(":")) : state;
      const adminTargeted = state.includes(":");
      if (uid !== stateUid) {
        setStatus("error");
        setError("Session mismatch — refusing to attach GSC to a different account.");
        return;
      }

      // Use direct fetch so we can read the JSON error body from non-2xx
      // responses (supabase.functions.invoke swallows it on iOS Safari).
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      if (!accessToken) {
        setStatus("error");
        setError("Session expired. Please sign in again and reconnect.");
        return;
      }
      let payload: any = null;
      try {
        const resp = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/exchange-gsc-code`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
              apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            },
            body: JSON.stringify({ code, state, redirectUri: buildGscRedirectUri() }),
          },
        );
        payload = await resp.json().catch(() => null);
        if (!resp.ok || payload?.ok === false) {
          setStatus("error");
          setError(payload?.error || `GSC exchange failed (${resp.status}).`);
          return;
        }
      } catch (e: any) {
        setStatus("error");
        setError(e?.message || "Network error connecting Google Search Console.");
        return;
      }

      // A self-connect goes to the property picker. An admin-targeted reconnect writes to
      // ANOTHER client, so show a success screen (the admin then clicks their next link;
      // the property is set + verified server-side afterward).
      if (adminTargeted) {
        setStatus("done");
        setConnectedInfo(payload?.connectedEmail ? `${payload.connectedEmail} → client ${payload?.clientId ?? ""}` : `client ${payload?.clientId ?? ""}`);
        return;
      }
      navigate("/gsc-select-property", { replace: true });
    })();
  }, [navigate]);

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-background text-foreground">
      <div className="max-w-md text-center px-4">
        {status === "working" ? (
          <>
            <div className="text-sm font-mono text-muted-foreground">
              Connecting Google Search Console…
            </div>
          </>
        ) : status === "done" ? (
          <>
            <div className="text-sm font-mono text-green-500 mb-2">
              Connected ✓ {connectedInfo || ""}
            </div>
            <div className="text-xs font-mono text-muted-foreground">
              Tokens saved. You can now click your next pre-targeted link (or close this tab).
            </div>
          </>
        ) : (
          <>
            <div className="text-sm font-mono text-red-500 mb-3">
              {error || "Could not connect Google Search Console."}
            </div>
            <button
              onClick={() => navigate("/dashboard")}
              className="text-xs font-mono underline text-muted-foreground"
            >
              Back to dashboard
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default GscCallback;
